// page-revision-challenge.ts — Challenger IA : ce qui va être transmis.
//
// Cette modale est **obligatoire et sans échappatoire** entre la décision de
// soumettre une revue à une IA et le départ effectif. Elle n'est pas une case de
// consentement à cocher au pas de course : elle doit ressembler à un document
// qu'on parcourt. C'est autant l'argument commercial de la fonctionnalité que sa
// garantie juridique.
//
// Elle reprend délibérément la modale de la revue du référentiel
// (`page-backlog-review-dossier.ts`), y compris ses trois choix de rédaction :
//
// 1. **Les compteurs sont le volume RÉEL**, pas une estimation. Ils sont
//    calculés par le serveur sur le dossier déjà construit — un « environ »
//    ruinerait la confiance que cet écran est censé établir.
// 2. **Le ⚠ est en `--warn`, jamais en `--danger`.** C'est une précision, pas
//    une alerte : les noms extérieurs écrits en toutes lettres ne figurent dans
//    aucune liste connue du tenant, donc ils ne sont pas substitués. On le dit,
//    et on donne à lire le contenu réel juste en dessous.
// 3. **Aucune emphase commerciale.** Des faits, une limite, et le dossier.
//
// Deux sorties, et leur place n'est pas décorative : le PIED porte la décision
// (« Transmettre », qui ouvre la confirmation de coût), le CORPS porte
// l'alternative (« Télécharger », qui n'appelle aucun fournisseur et ne coûte
// aucun crédit) et le dépôt du document de contexte.

import { gaugePercent, gaugeTone, mmss, remaining } from '../domain/challenge-wait.js';

interface ChallengeVolumes {
  risks: number;
  actions: number;
  people: number;
  risks_without_action: number;
  context_characters: number;
  characters: number;
}

interface ChallengeBody {
  dossier: Record<string, unknown>;
  volumes: ChallengeVolumes;
  // Le plafond de l'appel au modèle, en secondes. Il vient du SERVEUR, jamais
  // d'une constante recopiée ici : la jauge d'attente s'en sert comme point de
  // départ, et une valeur périmée en ferait un compte à rebours qui ment.
  timeout_seconds?: number;
}

// Au-delà, le navigateur peine à peindre le bloc et l'utilisateur ne le lira pas
// jusqu'au bout de toute façon. On tronque l'AFFICHAGE, jamais le dossier — et
// on le dit, parce qu'un aperçu silencieusement coupé serait un mensonge sur ce
// qui part.
const PREVIEW_CHARS = 60000;

// Doit rester égal à `analysis.CREDITS` côté serveur. Le libellé du bouton porte
// le coût : l'annoncer faux serait pire que ne pas l'annoncer.
const CREDITS = 5;

// L'état de la modale ouverte. Porté en module plutôt que re-passé partout : on
// n'en montre jamais deux à la fois.
let _revisionId = 0;
let _contextFile: File | null = null;
// Le plafond annoncé par le serveur au dernier chargement du dossier. Le repli
// n'a vocation à servir que face à un serveur plus ancien qui ne le rendrait
// pas : mieux vaut une jauge approximative que pas de repère du tout.
let _timeoutSeconds = 240;
// Le tic du décompte. Un seul à la fois, et toujours arrêté — un intervalle
// laissé courir écrirait dans une modale qui n'existe plus.
let _waitTimer: number | null = null;

function _countersHtml(v: ChallengeVolumes): string {
  const parts = [
    t('challenge.countRisks', { n: String(v.risks) }),
    t('challenge.countActions', { n: String(v.actions) }),
    t('challenge.countChars', { n: v.characters.toLocaleString('fr-FR') }),
  ];
  return `<div style="font-size:var(--fs-lg);font-weight:600;margin-bottom:12px">
      ${parts.map((p) => esc(p)).join(' &nbsp;·&nbsp; ')}
    </div>`;
}

function _lineHtml(mark: string, color: string, text: string): string {
  return `<div style="display:flex;gap:8px;margin:6px 0;font-size:var(--fs-sm)">
      <span style="color:${color};font-weight:700">${esc(mark)}</span>
      <span>${esc(text)}</span>
    </div>`;
}

function _guaranteesHtml(v: ChallengeVolumes): string {
  // « les 0 personnes du périmètre sont remplacées » se lit mal et sonne faux.
  // Une revue sans porteur ni RACI n'a rien à substituer : autant le dire.
  const people = v.people
    ? t('challenge.okPeople', { n: String(v.people) })
    : t('challenge.okNoPeople');
  // Le nombre de risques sans plan n'est PAS un reproche affiché ici : c'est le
  // volume de ce qui part, au même titre que les autres compteurs. Le jugement,
  // c'est le document qui le portera.
  const gaps = v.risks_without_action
    ? _lineHtml(
        'ℹ',
        'var(--text3)',
        t('challenge.infoNoPlan', { n: String(v.risks_without_action) }),
      )
    : '';
  return `<div style="margin-bottom:12px">
      ${_lineHtml('✓', 'var(--accent)', people)}
      ${_lineHtml('✓', 'var(--accent)', t('challenge.okIds'))}
      ${_lineHtml('⚠', 'var(--warn)', t('challenge.warnExternal'))}
      ${gaps}
    </div>`;
}

function _contextHtml(v: ChallengeVolumes): string {
  const chosen = _contextFile
    ? `<span style="font-size:var(--fs-sm)">${esc(_contextFile.name)}</span>
       <button class="btn btn-secondary btn-sm" id="btn-context-clear">${esc(t('challenge.contextClear'))}</button>`
    : `<span class="text-muted text-xs">${esc(t('challenge.contextHint'))}</span>`;
  const included = v.context_characters
    ? `<div class="text-muted text-xs" style="margin-top:4px">${esc(
        t('challenge.contextIncluded', {
          n: v.context_characters.toLocaleString('fr-FR'),
        }),
      )}</div>`
    : '';
  return `<div style="border:1px solid var(--border);border-radius:4px;padding:10px;margin-bottom:12px">
      <div style="font-weight:600;font-size:var(--fs-sm);margin-bottom:6px">${esc(t('challenge.contextTitle'))}</div>
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">
        <button class="btn btn-secondary btn-sm" id="btn-context-pick">${esc(t('challenge.contextPick'))}</button>
        ${chosen}
      </div>
      ${included}
      <input type="file" id="challenge-context-input" accept=".docx,.pptx" style="display:none">
    </div>`;
}

function _previewHtml(dossier: Record<string, unknown>): string {
  const full = JSON.stringify(dossier, null, 2);
  const shown = full.length > PREVIEW_CHARS ? full.slice(0, PREVIEW_CHARS) : full;
  const notice =
    full.length > PREVIEW_CHARS
      ? `<div class="text-muted text-xs" style="margin-top:6px">${esc(
          t('challenge.previewTruncated', {
            shown: String(PREVIEW_CHARS),
            total: String(full.length),
          }),
        )}</div>`
      : '';
  return `<pre class="text-mono" style="font-size:var(--fs-xs);max-height:34vh;overflow:auto;
      background:var(--bg2);padding:10px;border-radius:4px;white-space:pre-wrap;
      word-break:break-word">${esc(shown)}</pre>${notice}`;
}

/** Le corps de la modale, déjà composé et échappé.
 *
 *  Une FONCTION plutôt qu'une variable intermédiaire : `innerHTML = <variable>`
 *  est interdit par ESLint quel que soit le nom de la variable — la règle lit
 *  une forme syntaxique, pas une convention. Le suffixe `Html` reste ce qui rend
 *  la sûreté auditable à la lecture. */
function _bodyHtml(data: ChallengeBody): string {
  return (
    _countersHtml(data.volumes) +
    _guaranteesHtml(data.volumes) +
    _contextHtml(data.volumes) +
    _previewHtml(data.dossier) +
    // La sortie latérale : télécharger et soumettre à l'IA de son choix, sans
    // consommer un crédit. Dans le CORPS, pas dans le pied — le pied porte la
    // décision, ceci porte une alternative.
    `<div style="margin-top:12px;display:flex;gap:10px;align-items:center;flex-wrap:wrap">
        <button class="btn btn-secondary" id="btn-kit-pseudo">${esc(t('challenge.downloadPseudo'))}</button>
        <button class="btn btn-secondary" id="btn-kit-clear">${esc(t('challenge.downloadClear'))}</button>
        <span class="text-muted text-xs">${esc(t('challenge.kitHint'))}</span>
      </div>`
  );
}

/** Ouvre la modale de vérification. */
export function openChallengeModal(revisionId: number): void {
  _revisionId = revisionId;
  openWideModal(
    t('challenge.title'),
    `<div class="loading">${esc(t('common.loading'))}</div>`,
    () => _confirmChallenge(revisionId),
    t('challenge.send'),
  );
  void _load(revisionId);
}

/** Le dernier arrêt avant l'envoi : le format de sortie, et ce que ça coûte.
 *
 *  Le format est demandé ICI et non dans la première modale : c'est une décision
 *  de sortie, pas une donnée à relire. */
function _confirmChallenge(revisionId: number): void {
  openModal(
    t('challenge.confirmTitle'),
    `<div style="margin-bottom:12px">
       <div style="font-weight:600;font-size:var(--fs-sm);margin-bottom:6px">${esc(t('challenge.formatLabel'))}</div>
       <label style="margin-right:16px"><input type="radio" name="challenge-format" value="docx" checked> ${esc(t('challenge.formatDocx'))}</label>
       <label><input type="radio" name="challenge-format" value="pptx"> ${esc(t('challenge.formatPptx'))}</label>
     </div>
     <p>${esc(t('challenge.confirmBody', { n: String(CREDITS) }))}</p>
     <p class="text-muted text-xs">${esc(t('challenge.confirmHint'))}</p>`,
    () => _send(revisionId),
    t('challenge.send'),
  );
  // Annuler et la croix rouvrent la modale du dossier : on n'a pas voulu
  // envoyer, on n'a pas voulu perdre sa lecture.
  const reopen = (): void => openChallengeModal(revisionId);
  document.getElementById('modal-cancel')?.addEventListener('click', reopen);
  document.getElementById('modal-close')?.addEventListener('click', reopen);
}

async function _load(revisionId: number): Promise<void> {
  const body = document.getElementById('modal-body');
  if (!body) return;
  try {
    // Avec un document de contexte, c'est le serveur qui dit ce qu'il apporte
    // une fois extrait ET pseudonymisé : un compteur calculé côté navigateur
    // serait une estimation, donc un mensonge sur ce qui part.
    let data: ChallengeBody;
    if (_contextFile) {
      const form = new FormData();
      form.append('context', _contextFile);
      data = (await api.previewChallengeDossier(revisionId, form)) as ChallengeBody;
    } else {
      data = (await api.getChallengeDossier(revisionId)) as ChallengeBody;
    }
    if (typeof data.timeout_seconds === 'number' && data.timeout_seconds > 0) {
      _timeoutSeconds = Math.round(data.timeout_seconds);
    }
    body.innerHTML = _bodyHtml(data);
    _wireBody(revisionId);
  } catch (e: unknown) {
    body.replaceChildren();
    const err = document.createElement('div');
    err.className = 'error';
    err.textContent = e instanceof Error ? e.message : t('common.error');
    body.appendChild(err);
  }
}

function _wireBody(revisionId: number): void {
  const input = document.getElementById('challenge-context-input') as HTMLInputElement | null;
  document.getElementById('btn-context-pick')?.addEventListener('click', () => input?.click());
  input?.addEventListener('change', () => {
    _contextFile = input.files?.[0] || null;
    // On recharge : le serveur seul sait ce que le document apporte réellement
    // au dossier une fois extrait et pseudonymisé. Afficher un compteur calculé
    // côté navigateur serait une estimation, donc un mensonge.
    void _load(revisionId);
  });
  document.getElementById('btn-context-clear')?.addEventListener('click', () => {
    _contextFile = null;
    void _load(revisionId);
  });
  document
    .getElementById('btn-kit-pseudo')
    ?.addEventListener('click', () => void _downloadKit(true));
  document
    .getElementById('btn-kit-clear')
    ?.addEventListener('click', () => void _downloadKit(false));
}

async function _downloadKit(pseudonymized: boolean): Promise<void> {
  const id = pseudonymized ? 'btn-kit-pseudo' : 'btn-kit-clear';
  const btn = document.getElementById(id) as HTMLButtonElement | null;
  // Ces boutons VIVENT DANS LE CORPS de la modale, pas dans son pied : personne
  // ne les désactive à notre place, donc `withButton` a bien un rôle ici.
  await api.withButton(btn, async () => {
    try {
      const out = await api.downloadChallengeKit(
        _revisionId,
        pseudonymized,
        `kit-challenge-${_revisionId}.zip`,
      );
      toast(t('challenge.downloaded', { name: out.filename }));
    } catch (e: unknown) {
      // La modale reste ouverte : l'utilisateur doit pouvoir réessayer sans
      // reconstruire le dossier.
      toast(e instanceof Error ? e.message : t('common.error'), 'error');
    }
  });
}

/** L'attente, pendant que le modèle travaille.
 *
 *  Une VRAIE popup et non un simple bouton grisé, parce que la génération est
 *  synchrone et que le document n'est produit qu'une fois : la réponse EST le
 *  livrable, rien n'en est conservé côté Ornexia. Quitter la page pendant
 *  l'analyse, c'est perdre le document et les crédits qui l'ont payé — l'écran
 *  doit donc le dire, pas le laisser deviner.
 *
 *  Ni « Annuler » ni « Fermer » au pied : la requête est partie et rien ne
 *  l'arrête côté serveur. Un bouton qui n'annule rien serait un mensonge.
 */
function _waitBodyHtml(total: number): string {
  return `<div class="loading">${esc(t('challenge.waitBody'))}</div>
     <div style="height:6px;background:var(--bg3);border-radius:3px;margin:4px 0 6px">
       <div id="challenge-wait-gauge" style="height:6px;width:100%;background:var(--accent);
         border-radius:3px;transition:width 1s linear"></div>
     </div>
     <div id="challenge-wait-left" class="text-xs text-muted" style="text-align:center">${esc(
       t('challenge.waitRemaining', { time: mmss(total) }),
     )}</div>
     <p class="text-muted text-xs" style="text-align:center;margin-top:10px">${esc(
       t('challenge.waitStay'),
     )}</p>`;
}

function _stopWaitTimer(): void {
  if (_waitTimer !== null) {
    window.clearInterval(_waitTimer);
    _waitTimer = null;
  }
}

/** L'échec s'affiche DANS la popup d'attente, qui reste ouverte.
 *
 *  Un toast serait le pire support pour ce message : l'analyse dure plusieurs
 *  minutes, l'utilisateur est parti faire autre chose, et il reviendrait devant
 *  un écran redevenu normal sans jamais savoir ce qui s'est passé. La popup, à
 *  l'inverse, l'attend. Elle ne se referme que sur son geste — d'où le
 *  « Fermer », révélé ici et masqué pendant l'attente : rien à fermer tant que
 *  le travail continue.
 */
function _showWaitError(message: string): void {
  _stopWaitTimer();
  const body = document.getElementById('modal-body');
  // La popup a pu être fermée pendant l'attente. Le toast redevient alors le
  // seul support disponible — faute de mieux, pas par choix.
  if (!body) {
    toast(message, 'error');
    return;
  }
  const title = document.getElementById('modal-title');
  if (title) title.textContent = t('challenge.errorTitle');

  body.replaceChildren();
  const box = document.createElement('div');
  box.className = 'error';
  // Le message du SERVEUR, tel quel : consentement absent, solde insuffisant,
  // modèle indisponible, chacun porte son action utile. Le réécrire ici les
  // rabattrait tous sur « une erreur est survenue ».
  box.textContent = message;
  const hint = document.createElement('p');
  hint.className = 'text-muted text-xs';
  hint.style.marginTop = '10px';
  hint.textContent = t('challenge.errorHint');
  body.append(box, hint);

  const confirm = document.getElementById('modal-confirm');
  if (confirm) confirm.style.display = '';
}

/** L'attente, pendant que le modèle travaille — avec un repère de temps.
 *
 *  La jauge part du plafond ANNONCÉ PAR LE SERVEUR et se vide. Elle ne promet
 *  pas que l'analyse durera tout ce temps : elle borne l'attente, ce qui est la
 *  seule question de celui qui patiente. Finir avec une minute au compteur
 *  rassure ; attendre sans savoir combien de temps, jamais.
 */
function _openWaitModal(): void {
  const total = _timeoutSeconds;
  openModal(t('challenge.waitTitle'), _waitBodyHtml(total), () => closeModal(), t('common.close'));
  // Ni « Annuler » ni « Fermer » au pied : la requête est partie et rien ne
  // l'arrête côté serveur. Un bouton qui n'annule rien serait un mensonge.
  for (const id of ['modal-cancel', 'modal-confirm']) {
    const btn = document.getElementById(id);
    if (btn) btn.style.display = 'none';
  }

  const startedAt = Date.now();
  _stopWaitTimer();
  _waitTimer = window.setInterval(() => {
    const gauge = document.getElementById('challenge-wait-gauge');
    const label = document.getElementById('challenge-wait-left');
    // La modale a été fermée sous nos pieds (croix, Échap, clic dehors) : on
    // s'arrête plutôt que d'écrire dans un DOM disparu.
    if (!gauge || !label) {
      _stopWaitTimer();
      return;
    }
    const left = remaining(total, (Date.now() - startedAt) / 1000);
    gauge.style.width = `${gaugePercent(total, left)}%`;
    gauge.style.background = gaugeTone(left);
    label.textContent = t('challenge.waitRemaining', { time: mmss(left) });
    if (left <= 0) _stopWaitTimer();
  }, 1000);
}

async function _send(revisionId: number): Promise<void> {
  // Le format est lu AVANT de remplacer la modale : il vit dans le corps de la
  // confirmation, qui disparaît avec elle.
  const picked = document.querySelector<HTMLInputElement>('input[name="challenge-format"]:checked');
  const format = picked?.value === 'pptx' ? 'pptx' : 'docx';
  const form = new FormData();
  form.append('output_format', format);
  if (_contextFile) form.append('context', _contextFile);

  _openWaitModal();
  try {
    const name = await api.challengeRevision(revisionId, form, `challenge.${format}`);
    _stopWaitTimer();
    closeModal();
    toast(t('challenge.done', { name }));
  } catch (e: unknown) {
    // L'échec reste DANS la popup, et elle reste ouverte. Rouvrir la
    // confirmation par-dessus, comme on le faisait, remplaçait le message par
    // un formulaire : l'utilisateur revenu d'ailleurs voyait un écran de départ
    // sans jamais lire ce qui avait échoué.
    _showWaitError(e instanceof Error ? e.message : t('common.error'));
  }
}

declare global {
  interface Window {
    openChallengeModal: typeof openChallengeModal;
  }
}

window.openChallengeModal = openChallengeModal;
